import type { ComponentType } from 'react';
import { EditAction, Part, type DomainTabProps } from './shared';
import { DomainServicesTab } from '../../services/DomainServicesTab';
import { BillingTab, DnssecTab, InvoicesTab, OrdersTab, TransfersTab } from './CoreTabs';
import { AuditTab, ComplianceTab, LifecycleTab, RegistryTab, VnnicTab } from './DetailTabs';
import { HistoryTab } from './HistoryTab';
import type { DomainTabId } from './moved';
import { ObservabilityTab } from './ObservabilityTab';
import { OverviewTab } from './OverviewTab';
import { SubjectContactsTab } from './SubjectContactsTab';
import { DossierIdentityTab } from './DossierIdentityTab';
import { DnssecSummary, PrivacyView } from './ServiceViews';
import { DnsTab } from './DnsTab';
import { DossierNotes, FeeCalculator, FinanceFigures, RegistryStatePanels, RenewalPanel, SecurityPanel, SyncComparePanel } from './PartsV21';

function Dnssec({ detail }: DomainTabProps) {
  return (
    <>
      <DnssecSummary detail={detail} />
      <DnssecTab detail={detail} extraActions={<EditAction detail={detail} action="dnssec" />} />
    </>
  );
}

function Addons(p: DomainTabProps) {
  return (
    <>
      <DomainServicesTab {...p} />
      <Part id="privacy">
        <PrivacyView {...p} />
      </Part>
      <FeeCalculator detail={p.detail} />
    </>
  );
}

function Finance(p: DomainTabProps) {
  return (
    <>
      <FinanceFigures detail={p.detail} />
      <Part id="orders">
        <OrdersTab {...p} />
      </Part>
      <Part id="invoices">
        <InvoicesTab {...p} />
      </Part>
      <BillingTab {...p} />
    </>
  );
}

function Lifecycle(p: DomainTabProps) {
  return (
    <>
      <LifecycleTab {...p} />
      <RenewalPanel detail={p.detail} />
    </>
  );
}

function Transfer(p: DomainTabProps) {
  return (
    <>
      <SecurityPanel detail={p.detail} />
      <Part id="transfers">
        <TransfersTab {...p} />
      </Part>
    </>
  );
}

function Registry(p: DomainTabProps) {
  return (
    <>
      <RegistryStatePanels detail={p.detail} reload={p.reload} />
      <Part id="commands">
        <RegistryTab {...p} />
      </Part>
      <Part id="monitoring">
        <ObservabilityTab {...p} />
      </Part>
    </>
  );
}

function Compliance(p: DomainTabProps) {
  return (
    <>
      <SyncComparePanel detail={p.detail} />
      <ComplianceTab {...p} />
      <Part id="vnnic">
        <VnnicTab {...p} />
      </Part>
    </>
  );
}

function History(p: DomainTabProps) {
  return (
    <>
      <HistoryTab {...p} />
      <DossierNotes detail={p.detail} />
      <Part id="audit">
        <AuditTab {...p} />
      </Part>
    </>
  );
}

/** The component of each tab (order and labels: `moved.ts`, `domainPage:tabs.*`). */
export const DOMAIN_TABS: Readonly<Record<DomainTabId, ComponentType<DomainTabProps>>> = {
  overview: OverviewTab,
  subject: SubjectContactsTab,
  dossier: DossierIdentityTab,
  dns: DnsTab,
  dnssec: Dnssec,
  addons: Addons,
  finance: Finance,
  lifecycle: Lifecycle,
  transfer: Transfer,
  registry: Registry,
  compliance: Compliance,
  history: History,
};

export { EditAction, Part, type DomainTabProps } from './shared';
